import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { isUserAdmin } from '../utils/adminUtils';

interface AdminSettings {
  showCacheDebug: boolean;
  showDatabaseBanner: boolean;
  showWebSocketStatus: boolean;
  showPerformanceMetrics: boolean;
  showFeatureFlags: boolean;
}

interface AdminSettingsContextType {
  settings: AdminSettings;
  updateSetting: (key: keyof AdminSettings, value: boolean) => void;
  resetSettings: () => void;
  isAdmin: boolean;
}

const defaultSettings: AdminSettings = {
  showCacheDebug: false,
  showDatabaseBanner: false,
  showWebSocketStatus: false,
  showPerformanceMetrics: false,
  showFeatureFlags: false,
};

const AdminSettingsContext = createContext<AdminSettingsContextType | undefined>(undefined);

export function AdminSettingsProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);
  const [settings, setSettings] = useState<AdminSettings>(defaultSettings);

  // Check admin status
  useEffect(() => {
    const checkAdminStatus = async () => {
      if (user) {
        const adminStatus = await isUserAdmin(user);
        setIsAdmin(adminStatus);
      } else {
        setIsAdmin(false);
      }
    };
    
    checkAdminStatus();
  }, [user]);

  // Load settings from localStorage when admin status is confirmed
  useEffect(() => {
    if (isAdmin) {
      const savedSettings = localStorage.getItem('adminDebugSettings');
      if (savedSettings) {
        try {
          const parsed = JSON.parse(savedSettings);
          setSettings({ ...defaultSettings, ...parsed });
        } catch (error) {
          console.error('Failed to parse admin settings:', error);
        }
      }
    }
  }, [isAdmin]);

  const updateSetting = (key: keyof AdminSettings, value: boolean) => {
    if (!isAdmin) return;
    
    const newSettings = { ...settings, [key]: value };
    setSettings(newSettings);
    localStorage.setItem('adminDebugSettings', JSON.stringify(newSettings));
  };

  const resetSettings = () => {
    if (!isAdmin) return;
    
    setSettings(defaultSettings);
    localStorage.removeItem('adminDebugSettings');
  };

  return (
    <AdminSettingsContext.Provider value={{ settings, updateSetting, resetSettings, isAdmin }}>
      {children}
    </AdminSettingsContext.Provider>
  );
}

export function useAdminSettings() {
  const context = useContext(AdminSettingsContext);
  if (context === undefined) {
    throw new Error('useAdminSettings must be used within an AdminSettingsProvider');
  }
  return context;
}